import type {
  ISearchTermsGroup,
  ISearchTermsGrowth,
  SearchTermTrend,
} from "../../types";

// Termos que as pessoas digitaram no Google e mostraram o perfil, mês a mês —
// a "Discriminação das pesquisas" do painel do Google (pedido de 14/09/2026).

const MESES = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

/** "2026-08" → "ago/26" */
export const rotuloDoMes = (mes: string): string => {
  const [ano, numero] = mes.split("-");
  const indice = Number(numero) - 1;
  if (!ano || Number.isNaN(indice) || indice < 0 || indice > 11) return mes;
  return `${MESES[indice]}/${ano.slice(-2)}`;
};

/**
 * Selo ao lado do termo. Regra atual do produto: só crescimento aparece.
 * `mostrarQueda` fica para a tela de clientes vencidos (winback).
 */
export const seloDeCrescimento = (
  growthPercent: number | null | undefined,
  trend: SearchTermTrend,
  opcoes: { mostrarQueda?: boolean } = {},
): string | null => {
  if (trend === "NEW") return "Novo";
  if (typeof growthPercent !== "number") return null;
  if (growthPercent > 0) return `+${growthPercent}%`;
  if (opcoes.mostrarQueda && growthPercent < 0) return `${growthPercent}%`;
  return null;
};

export const rotuloDoGrupo = (
  grupo: Pick<ISearchTermsGroup, "kind" | "name">,
): string => {
  if (grupo.kind === "BRAND") return "Pelo nome da empresa";
  if (grupo.kind === "ADDRESS") return "Pelo endereço";
  return grupo.name;
};

/** Os maiores grupos que tiveram alguma busca (a API já manda do maior para o menor). */
export const gruposDoGrafico = (
  grupos: ISearchTermsGroup[],
  limite = 4,
): ISearchTermsGroup[] =>
  grupos.filter((grupo) => grupo.values.some((valor) => (valor ?? 0) > 0)).slice(0, limite);

/** Termos que o Google só mostrou como "menos de N" (sem número exato em nenhum mês). */
export const termosComPoucoVolume = (resposta: ISearchTermsGrowth): number =>
  resposta.terms.filter(
    (termo) =>
      termo.values.every((valor) => valor === null || valor === undefined) &&
      termo.belowThreshold.some(Boolean),
  ).length;

/**
 * O Google libera o mês que acabou de fechar com atraso, e a API corta esse mês
 * da série. Devolve o rótulo do mês que ainda falta (para avisar) ou null.
 */
export const mesAindaNaoLiberado = (
  months: string[],
  hoje: Date = new Date(),
): string | null => {
  if (!months.length) return null;
  const esperado = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
  const chave = `${esperado.getFullYear()}-${String(esperado.getMonth() + 1).padStart(2, "0")}`;
  return months[months.length - 1] < chave ? rotuloDoMes(chave) : null;
};

export const temBuscas = (
  resposta: ISearchTermsGrowth | null | undefined,
): resposta is ISearchTermsGrowth =>
  !!resposta &&
  resposta.terms.length > 0 &&
  resposta.totalByMonth.some((valor) => valor > 0);
